import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  untracked,
  viewChild
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { TaskPrioritySelectComponent } from '@app/components/task/task-priority-select/task-priority-select.component';
import { TaskRowActionsMenuComponent } from '@app/components/task/task-row-actions-menu/task-row-actions-menu.component';
import { LocationSelectComponent } from '@app/components/task/task-state-selectors/location-select/location-select.component';
import { TaskIndustrySelectComponent } from '@app/components/task/task-state-selectors/task-industry-select/task-industry-select.component';
import { TaskStatusSelectComponent } from '@app/components/task/task-state-selectors/task-status-select/task-status-select.component';
import {
  TaskTimerComponent,
  TaskTimerMode
} from '@app/components/task/task-state-selectors/task-timer/task-timer.component';
import { TaskTypeSelectComponent } from '@app/components/task/task-state-selectors/task-type-select/task-type-select.component';
import { TaskScheduleActionsService } from '@app/shared/services/actions/task-schedule-actions.service';
import { TagsDataService } from '@app/shared/services/tags-data.service';
import { TaskParticipantsDataService } from '@app/shared/services/task-participants-data.service';
import { TasksDataService } from '@app/shared/services/tasks-data.service';
import { SortDirection } from '@app/shared/types/enums/sort-direction.enum';
import { TaskPriority } from '@app/shared/types/enums/task-priority.enum';
import { TaskSortField } from '@app/shared/types/enums/task-sort-field';
import { TaskStatus } from '@app/shared/types/enums/task-status.enum';
import { ProjectParticipant } from '@app/shared/types/models/project/project-participant';
import { Tag } from '@app/shared/types/models/tag/tag.model';
import { TaskStatusSelectEnum } from '@app/shared/types/models/task/task-status.model';
import { TaskType } from '@app/shared/types/models/task/task-type.model';
import { Task } from '@app/shared/types/models/task/task.model';
import { UiButtonIconDirective } from '@app/shared/ui/button/ui-button-icon.directive';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';
import { UiDatePickerCompactComponent } from '@app/shared/ui/date-picker-compact/ui-date-picker-compact.component';
import { PROFFEO_UI_TABLE_IDS } from '@app/shared/ui/table/ui-table-ids';
import { UiTablePreferencesService } from '@app/shared/ui/table/ui-table-preferences.service';
import { UiTableComponent } from '@app/shared/ui/table/ui-table.component';
import { UiTaskAssigneesComponent } from '@app/shared/ui/task-assignees/ui-task-assignees.component';
import { timelineGeometry, timelineWindow } from '@app/shared/ui/timeline/timeline-days.util';
import { UiTimelineComponent } from '@app/shared/ui/timeline/ui-timeline.component';
import {
  PROFFEO_UI_TABLE_COLUMN_ROLE_TABLE_MANAGEMENT,
  ProffeoUiTableCellTemplateContext,
  ProffeoUiTableColumn,
  ProffeoUiTableSort
} from '@app/shared/ui/types/table.types';
import { scheduleControlDay, schedulePeriod } from '@app/shared/utils/task-schedule.util';
import { ActiveTaskService } from '@app/views/company/projects/tasks/active-task.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  groupTaskPage,
  taskGroupId,
  TaskGroupingField,
  taskGroupLabel,
  taskGroupSignature
} from '../task-grouping.util';
import { TaskFiltersService } from '../task-list-toolbar/task-filters.service';
import { TaskTableTagsComponent } from './task-table-tags/task-table-tags.component';
import {
  flattenTaskTableRows,
  mapTaskTableSortChange,
  resolveTaskTableSort,
  TaskTableRow,
  taskTableRowsShareIndustry,
  taskTableRowsShareLocation,
  taskTableRowsSharePriority,
  taskTableRowsShareStatus,
  TaskTableSortChange
} from './task-table.utils';

/**
 * @summary Desktop task table renderer with card-parity columns and recursive subtasks.
 * @selector proffeo-task-table
 */
@Component({
  selector: 'proffeo-task-table',
  templateUrl: './task-table.component.html',
  styleUrl: './task-table.component.scss',
  imports: [
    MatIconModule,
    MatTooltip,
    TranslatePipe,
    UiTableComponent,
    UiTimelineComponent,
    UiButtonComponent,
    UiButtonIconDirective,
    TaskTableTagsComponent,
    UiDatePickerCompactComponent,
    UiTaskAssigneesComponent,
    TaskStatusSelectComponent,
    TaskPrioritySelectComponent,
    TaskTypeSelectComponent,
    TaskIndustrySelectComponent,
    LocationSelectComponent,
    TaskTimerComponent,
    TaskRowActionsMenuComponent
  ],
  host: {
    class: 'flex min-h-0 min-w-0 h-full w-full flex-col'
  }
})
export class TaskTableComponent {
  private readonly translateService = inject(TranslateService);
  private readonly preferences = inject(UiTablePreferencesService);
  private readonly schedule = inject(TaskScheduleActionsService, { optional: true });
  private readonly tasksDataService = inject(TasksDataService);
  private readonly tagsDataService = inject(TagsDataService);
  private readonly taskParticipantsService = inject(TaskParticipantsDataService);
  private readonly activeTaskService = inject(ActiveTaskService);

  private readonly expandHeaderTemplate = viewChild<TemplateRef<unknown>>('expandHeader');
  private readonly expandCellTemplate =
    viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<TaskTableRow>>>('expandCell');
  private readonly avatarHeaderTemplate = viewChild<TemplateRef<unknown>>('avatarHeader');
  private readonly avatarCellTemplate =
    viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<TaskTableRow>>>('avatarCell');
  private readonly numberCellTemplate =
    viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<TaskTableRow>>>('numberCell');
  private readonly nameCellTemplate =
    viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<TaskTableRow>>>('nameCell');
  private readonly tagsCellTemplate =
    viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<TaskTableRow>>>('tagsCell');
  private readonly statusCellTemplate =
    viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<TaskTableRow>>>('statusCell');
  private readonly priorityCellTemplate =
    viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<TaskTableRow>>>('priorityCell');
  private readonly assigneesCellTemplate =
    viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<TaskTableRow>>>('assigneesCell');
  private readonly industryCellTemplate =
    viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<TaskTableRow>>>('industryCell');
  private readonly locationCellTemplate =
    viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<TaskTableRow>>>('locationCell');
  private readonly timerCellTemplate =
    viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<TaskTableRow>>>('timerCell');
  private readonly progressCellTemplate =
    viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<TaskTableRow>>>('progressCell');
  private readonly startDateCellTemplate =
    viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<TaskTableRow>>>('startDateCell');
  private readonly endDateCellTemplate =
    viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<TaskTableRow>>>('endDateCell');
  private readonly actionsCellTemplate =
    viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<TaskTableRow>>>('actionsCell');

  private readonly timelineHeaderTemplate = viewChild<TemplateRef<unknown>>('timelineHeader');
  private readonly timelineCellTemplate =
    viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<TaskTableRow>>>('timelineCell');
  private readonly reconcileGrouping = effect(() => {
    if (!this.taskFilters) return;
    const grouping = this.taskFilters.taskGrouping();
    const groups = groupTaskPage(this.tasks(), grouping);
    untracked(() => this.taskFilters.reconcileTaskGroups(`${grouping}:${taskGroupSignature(groups)}`));
  });
  private readonly expandedTaskIds = signal<ReadonlySet<string>>(new Set());
  /** "Expand all" is on: subtasks that have their own subtasks open as soon as they load. */
  private readonly isExpandAllActive = signal(false);
  /** Rows already expanded once; a row the user collapses afterwards is not reopened by expand-all. */
  private readonly autoExpandedTaskIds = new Set<string>();
  private readonly uiTable = viewChild<UiTableComponent<TaskTableRow>>(UiTableComponent);

  private readonly baseColumns = computed((): ProffeoUiTableColumn<TaskTableRow>[] => [
    {
      columnId: 'expand',
      columnName: '',
      dataType: 'object',
      accessor: row => row.task,
      sortFunction: () => 0,
      sortable: false,
      hideable: false,
      technical: true,
      expandable: false,
      // Fits the subtask count chip with a two-digit count plus cell padding.
      widthPx: 68,
      align: 'center',
      template: this.expandCellTemplate(),
      headerTemplate: this.expandHeaderTemplate()
    },
    {
      columnId: 'avatar',
      columnName: this.translateService.instant('project-tasks.list.column-headers.avatar'),
      dataType: 'object',
      accessor: row => row.task,
      sortFunction: () => 0,
      sortable: false,
      hideable: true,
      technical: true,
      expandable: false,
      widthPx: 80,
      align: 'center',
      template: this.avatarCellTemplate(),
      headerTemplate: this.avatarHeaderTemplate()
    },
    {
      columnId: 'name',
      columnName: this.translateService.instant('project-tasks.list.column-headers.name'),
      dataType: 'string',
      accessor: row => row.task.name,
      sortable: true,
      autoWidthMaxChars: 30,
      minWidthChars: 30,
      holdAutoWidth: true,
      hideable: false,
      alwaysPinned: true,
      widthPx: 320,
      template: this.nameCellTemplate()
    },
    {
      columnId: 'number',
      columnName: this.translateService.instant('project-tasks.list.column-headers.number'),
      dataType: 'string',
      accessor: row => `${row.task.taskType()?.code ?? ''}-${row.task.taskNumber ?? ''}`,
      sortable: true,
      hideable: false,
      alwaysPinned: true,
      keepContentWidth: true,
      widthPx: 120,
      template: this.numberCellTemplate()
    },
    {
      columnId: 'industry',
      columnName: this.translateService.instant('project-tasks.list.column-headers.industry'),
      dataType: 'string',
      accessor: row => row.task.projectIndustry()?.industry()?.name ?? '',
      sortable: false,
      widthPx: 130,
      groupFunction: taskTableRowsShareIndustry,
      groupId: (_, rows) =>
        taskGroupId(
          'industry',
          rows.filter(row => row.depth === 0).map(row => row.task)
        ),
      groupCount: rows => rows.filter(row => row.depth === 0).length,
      groupLabel: representative => this.resolveSharedGroupLabel(representative, 'industry'),
      template: this.industryCellTemplate()
    },
    {
      columnId: 'priority',
      columnName: this.translateService.instant('project-tasks.list.column-headers.priority'),
      dataType: 'string',
      accessor: row => row.task.priority ?? '',
      sortable: true,
      clipHeaderLabel: true,
      expandable: false,
      widthPx: 40,
      groupFunction: taskTableRowsSharePriority,
      groupId: (_, rows) =>
        taskGroupId(
          'priority',
          rows.filter(row => row.depth === 0).map(row => row.task)
        ),
      groupCount: rows => rows.filter(row => row.depth === 0).length,
      groupLabel: representative => this.resolveSharedGroupLabel(representative, 'priority'),
      template: this.priorityCellTemplate()
    },
    {
      columnId: 'status',
      columnName: this.translateService.instant('project-tasks.list.column-headers.status'),
      dataType: 'string',
      accessor: row => row.task.status ?? '',
      sortable: true,
      widthPx: 120,
      groupFunction: taskTableRowsShareStatus,
      groupId: (_, rows) =>
        taskGroupId(
          'status',
          rows.filter(row => row.depth === 0).map(row => row.task)
        ),
      groupCount: rows => rows.filter(row => row.depth === 0).length,
      groupLabel: representative => this.resolveSharedGroupLabel(representative, 'status'),
      template: this.statusCellTemplate()
    },
    {
      columnId: 'location',
      columnName: this.translateService.instant('project-tasks.list.column-headers.location'),
      dataType: 'string',
      accessor: row => row.task.location()?.name ?? '',
      sortable: false,
      autoWidthMaxChars: 25,
      widthPx: 150,
      groupFunction: taskTableRowsShareLocation,
      groupId: (_, rows) =>
        taskGroupId(
          'location',
          rows.filter(row => row.depth === 0).map(row => row.task)
        ),
      groupCount: rows => rows.filter(row => row.depth === 0).length,
      groupLabel: representative => this.resolveSharedGroupLabel(representative, 'location'),
      template: this.locationCellTemplate()
    },
    {
      columnId: 'assignees',
      columnName: this.translateService.instant('project-tasks.list.column-headers.participants'),
      dataType: 'object',
      accessor: row => row.task,
      sortFunction: () => 0,
      sortable: false,
      keepContentWidth: true,
      // Three overlapping 32px avatars (88px) + the last avatar's ring + 6px cell padding each side.
      minWidthPx: 104,
      widthPx: 140,
      template: this.assigneesCellTemplate()
    },
    {
      columnId: 'timer',
      columnName: this.translateService.instant('project-tasks.list.column-headers.total-hours'),
      dataType: 'number',
      accessor: row => row.task.statistics?.summaryTime ?? 0,
      sortable: false,
      widthPx: 110,
      template: this.timerCellTemplate()
    },
    {
      columnId: 'progress',
      columnName: this.translateService.instant('project-tasks.list.column-headers.progress'),
      dataType: 'number',
      accessor: row => row.task.percentageOfProgress ?? 0,
      sortable: true,
      clipHeaderLabel: true,
      expandable: false,
      // Fits the widest value, "100 %" (~40px), plus the 6px cell padding on each side and rounding slack.
      widthPx: 56,
      template: this.progressCellTemplate()
    },
    {
      columnId: 'startDate',
      columnName: this.translateService.instant('project-tasks.list.column-headers.start-date'),
      dataType: 'date',
      accessor: row => row.task.startDate ?? null,
      sortable: true,
      // The compact picker renders its label in a flex cell, so header and value align left together.
      align: 'left',
      widthPx: 120,
      template: this.startDateCellTemplate()
    },
    {
      columnId: 'endDate',
      columnName: this.translateService.instant('project-tasks.list.column-headers.end-date'),
      dataType: 'date',
      accessor: row => row.task.endDate ?? null,
      sortable: true,
      align: 'left',
      widthPx: 120,
      template: this.endDateCellTemplate()
    },
    {
      columnId: 'tags',
      columnName: this.translateService.instant('project-tasks.list.column-headers.tags'),
      dataType: 'object',
      accessor: row => row.task,
      sortFunction: () => 0,
      sortable: false,
      fillRemaining: true,
      // One (truncated) tag chip plus the "+N" badge and cell padding, so a narrow table keeps a tag readable.
      minWidthPx: 104,
      widthPx: 160,
      template: this.tagsCellTemplate()
    },
    {
      columnId: 'actions',
      columnName: '',
      dataType: 'object',
      accessor: row => row.task,
      sortFunction: () => 0,
      sortable: false,
      hideable: false,
      expandable: false,
      pinnable: false,
      role: PROFFEO_UI_TABLE_COLUMN_ROLE_TABLE_MANAGEMENT,
      widthPx: 56,
      align: 'center',
      template: this.actionsCellTemplate()
    }
  ]);
  protected readonly taskFilters = inject(TaskFiltersService, { optional: true });
  protected readonly groupColumnId = computed(() => {
    const grouping = this.taskFilters?.taskGrouping();
    return !grouping || grouping === 'none' ? null : grouping;
  });

  protected readonly TaskStatusSelectEnum = TaskStatusSelectEnum;
  protected readonly TaskPriority = TaskPriority;
  protected readonly taskTimerMode = TaskTimerMode;
  protected readonly hasExpandedRows = computed(() => this.expandedTaskIds().size > 0);
  protected readonly subtasksToggleLabelKey = computed((): string =>
    this.hasExpandedRows() ? 'project-tasks.list.collapse-all-subtasks' : 'project-tasks.list.expand-all-subtasks'
  );
  protected readonly avatarColumnName = computed((): string =>
    this.translateService.instant('project-tasks.list.column-headers.avatar')
  );
  protected readonly avatarHideLabel = computed((): string =>
    this.translateService.instant('proffeo-ui-table.hide-column', { name: this.avatarColumnName() })
  );
  protected readonly tableRows = computed((): TaskTableRow[] =>
    flattenTaskTableRows(this.tasks(), this.expandedTaskIds(), task => {
      const projectId = task.project()?.id;
      const taskId = task.id;
      if (!projectId || !taskId) {
        return [];
      }

      return this.tasksDataService.getTasksByParent(projectId, taskId)() ?? [];
    })
  );

  protected readonly tableSort = computed((): ProffeoUiTableSort | null =>
    resolveTaskTableSort(this.orderBy(), this.order())
  );

  protected readonly selectedTableRows = computed((): TaskTableRow[] => {
    const activeTaskId = this.activeTaskService.activeTaskId();
    if (!activeTaskId) {
      return [];
    }

    return this.tableRows().filter(row => row.task.id === activeTaskId);
  });

  protected readonly locale = computed(() => this.translateService.currentLang() || 'en');
  protected readonly period = computed(
    () => this.schedule?.period() ?? schedulePeriod(scheduleControlDay(new Date()), 'month')
  );
  protected readonly todayDay = computed(() => this.schedule?.todayDay() ?? scheduleControlDay(new Date()));
  protected readonly calendarDay = scheduleControlDay;
  protected readonly columns = computed((): ProffeoUiTableColumn<TaskTableRow>[] => {
    const columns = this.baseColumns();
    if (!this.timelineEnabled()) return columns;
    const days = timelineWindow(this.period().from, this.period().to).count;
    const timeline: ProffeoUiTableColumn<TaskTableRow> = {
      columnId: 'timeline',
      columnName: this.translateService.instant('task-schedule.axis'),
      dataType: 'object',
      accessor: row => row.task,
      sortFunction: () => 0,
      sortable: false,
      hideable: false,
      pinnable: false,
      fillRemaining: true,
      widthPx: Math.max(800, days * 40),
      minWidthPx: days * 40,
      headerTemplate: this.timelineHeaderTemplate(),
      template: this.timelineCellTemplate()
    };
    return [
      ...columns
        .filter(column => column.columnId !== 'actions')
        .map(column => ({
          ...column,
          fillRemaining: false,
          visibleByDefault: ['expand', 'name', 'number'].includes(column.columnId)
        })),
      timeline,
      columns[columns.length - 1]
    ];
  });
  /** @summary Timeline preset of this table: separate `tasks.timeline` layout plus the calendar column. */
  public readonly timelineEnabled = input(false);
  /** @summary Root tasks for the current page. */
  public readonly tasks = input.required<readonly Task[]>();
  /** @summary Active company task types for type selectors. */
  public readonly taskTypes = input.required<TaskType[]>();
  /** @summary Current server-side sort field. */
  public readonly orderBy = input.required<TaskSortField>();
  /** @summary Current server-side sort direction. */
  public readonly order = input.required<SortDirection>();
  public readonly tableId = computed(() =>
    this.timelineEnabled() ? 'tasks.timeline' : PROFFEO_UI_TABLE_IDS.tasksList
  );
  /** @summary Column-manager menu of the table, opened from the task list toolbar. */
  public readonly columnManagerMenu = computed(() => this.uiTable()?.columnManagerMenu());

  public readonly statusChanged = output<{ status: TaskStatus; task: Task }>();
  public readonly priorityChanged = output<{ priority: TaskPriority; task: Task }>();
  public readonly taskStartDateSelected = output<{ date: Date; task: Task }>();
  public readonly taskEndDateSelected = output<{ date: Date; task: Task }>();
  public readonly industryClicked = output<Task>();
  public readonly locationClicked = output<Task>();
  public readonly changeGroupClicked = output<Task>();
  public readonly changeUsersClicked = output<Task>();
  public readonly sortChange = output<TaskTableSortChange>();

  public constructor() {
    effect(() => {
      if (!this.timelineEnabled()) return;
      untracked(() => {
        const previous = this.preferences.getPreferences('tasks.timeline')();
        const dateColumnIds = ['startDate', 'endDate'].filter(id =>
          previous?.layout.some(column => column.columnId === id)
        );
        this.preferences.setSessionColumnWidths('tasks.timeline', {});
        this.preferences.setPreferences('tasks.timeline', {
          version: 1,
          pinnedColumnIds: ['name', 'number'],
          groupByColumnId: null,
          layout: ['expand', 'name', 'number', ...dateColumnIds, 'timeline', 'actions'].map(columnId => ({
            columnId,
            relativeWidth: 1
          }))
        });
      });
    });
    effect(() => {
      if (!this.isExpandAllActive()) {
        return;
      }

      const collapsedParents = this.tableRows()
        .filter(row => row.hasChildren && !row.expanded && !!row.task.id && !this.autoExpandedTaskIds.has(row.task.id))
        .map(row => row.task);
      if (collapsedParents.length > 0) {
        untracked(() => this.expandTasks(collapsedParents));
      }
    });
  }

  protected rangeLabel(task: Task): string {
    const start = task.startDate ? scheduleControlDay(task.startDate) : null;
    const end = task.endDate ? scheduleControlDay(task.endDate) : null;
    return this.translateService.instant(
      `task-schedule.state.${timelineGeometry(this.period().from, this.period().to, start, end).state}`
    );
  }
  protected rangeTooltip(task: Task): string {
    return `${task.startDate ? scheduleControlDay(task.startDate) : '—'} – ${task.endDate ? scheduleControlDay(task.endDate) : '—'}`;
  }
  protected resolveRowId(row: TaskTableRow): string {
    return row.task.id ?? '';
  }

  private resolveSharedGroupLabel(row: TaskTableRow, field: TaskGroupingField): string {
    const value = taskGroupLabel(row.groupingTask ?? row.task, field);
    return value.translateLabel ? this.translateService.instant(value.label) : value.label;
  }
  protected onGroupColumnChanged(columnId: string | null): void {
    if (!this.taskFilters) return;
    if (columnId === null) this.taskFilters.taskGrouping.set('none');
    else if (['status', 'priority', 'industry', 'location'].includes(columnId))
      this.taskFilters.taskGrouping.set(columnId as TaskGroupingField);
  }
  protected onGroupExpansionChanged(ids: ReadonlySet<string>): void {
    this.taskFilters?.expandedTaskGroupIds.set(new Set(ids));
  }
  protected tagsForTask(taskId: string | undefined): Tag[] {
    if (!taskId) {
      return [];
    }

    return this.tagsDataService.getTaskTags(taskId)() ?? [];
  }

  protected taskUsers(taskId: string | undefined): ProjectParticipant[] {
    if (!taskId) {
      return [];
    }

    return (
      this.taskParticipantsService
        .getTaskParticipants(taskId)()
        ?.map(participant => participant.projectParticipant())
        .filter((participant): participant is ProjectParticipant => !!participant) ?? []
    );
  }

  protected hideAvatarColumn(event: Event): void {
    event.stopPropagation();
    this.uiTable()?.hideColumn('avatar');
  }

  protected indentStyle(depth: number): Record<string, string> {
    return { 'padding-left': `${depth * 1.25}rem` };
  }

  /**
   * Collapses every row when any is expanded; otherwise expands the whole subtask tree of the page.
   * Deeper levels load lazily, so the expand-all effect keeps opening them as they arrive.
   */
  protected toggleAllRows(): void {
    this.autoExpandedTaskIds.clear();
    if (this.hasExpandedRows()) {
      this.isExpandAllActive.set(false);
      this.expandedTaskIds.set(new Set());
      return;
    }

    this.isExpandAllActive.set(true);
    this.expandTasks(this.tasks().filter(task => (task.subtasksCount ?? 0) > 0));
  }

  protected onRowClick(row: TaskTableRow): void {
    void this.activeTaskService.setActiveTask(row.task);
  }

  protected toggleExpanded(task: Task, event: Event): void {
    event.stopPropagation();
    const taskId = task.id;
    if (!taskId) {
      return;
    }

    if (!this.expandedTaskIds().has(taskId)) {
      this.expandTasks([task]);
      return;
    }

    this.expandedTaskIds.update(ids => {
      const next = new Set(ids);
      next.delete(taskId);
      return next;
    });
    if (!this.hasExpandedRows()) {
      this.isExpandAllActive.set(false);
    }
  }

  /** Expands the given tasks and starts loading their subtasks. */
  private expandTasks(tasks: readonly Task[]): void {
    const next = new Set(this.expandedTaskIds());
    for (const task of tasks) {
      const taskId = task.id;
      if (!taskId) {
        continue;
      }

      this.autoExpandedTaskIds.add(taskId);
      next.add(taskId);
      const projectId = task.project()?.id;
      if (projectId) {
        this.tasksDataService.getTasksByParent(projectId, taskId);
      }
    }
    this.expandedTaskIds.set(next);
  }

  protected onSortChange(sort: ProffeoUiTableSort): void {
    const mapped = mapTaskTableSortChange(sort);
    if (!mapped) {
      return;
    }

    this.sortChange.emit(mapped);
  }

  protected emitStartDate(date: Date | null, task: Task): void {
    if (date) {
      this.taskStartDateSelected.emit({ date, task });
    }
  }

  protected emitEndDate(date: Date | null, task: Task): void {
    if (date) {
      this.taskEndDateSelected.emit({ date, task });
    }
  }
}
